import { Button, Icon, SimplifiedTooltip } from '@moleculeone/ui';
import { Analog } from '@retro-ui/api-contract';
import { styled } from '@stitches/react';
import { Spinner } from '@blueprintjs/core';

import { Compound } from './MoleculePreview';

const Container = styled('div', {
  display: 'flex',
  gap: '$5',
  alignItems: 'flex-start',
});

const GridContainer = styled('div', {
  flex: 1,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(15rem, 1fr))',
  gap: '1rem',
  paddingLeft: '$5',
  borderLeft: '2px solid $slate3',
});

const OriginalCompound = styled('div', {
  zIndex: 2,
  position: 'sticky',
  top: 0,
  left: 0,
});

const CompoundContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'white',
  borderRadius: '$medium',
  overflow: 'hidden',
  position: 'relative',
  padding: '$5',
  boxShadow: 'hsl(206 22% 7% / 15%) 0px 5px 10px -7px',
  variants: {
    selected: {
      false: {
        backgroundColor: '$slate3',
        opacity: 0.6,
      },
    },
    original: {
      true: {
        boxShadow: '$colors$purple4 0px 5px 19px -5px, $colors$purple4 0px 5px 10px -7px',
        marginBottom: '$5',
      },
      false: {
        'cursor': 'pointer',
        'transition': 'background-color 0.1s, opacity 0.1s, box-shadow .1s',
        '&:hover': {
          boxShadow: 'hsl(206 22% 7% / 15%) 0px 5px 19px -5px, hsl(206 22% 7% / 15%) 0px 5px 10px -7px',
        },
      },
    },
  },
  defaultVariants: {
    original: false,
  },
});

const Description = styled('div', {
  position: 'absolute',
  top: 0,
  right: 0,
});

const Name = styled('p', {
  margin: '$3 $5 0',
  wordBreak: 'break-all',
});

const SelectionIcon = styled(Icon, {
  position: 'absolute',
  top: '$4',
  left: '$4',
  fontSize: '$5',
  variants: {
    selected: {
      true: {
        color: '$green9',
      },
      false: {
        color: '$red9',
      },
    },
  },
});

const FetchingExplanationSpinner = styled(Spinner, {
  position: 'absolute',
  top: '50%',
  right: '50%',
  transform: 'translate(50%, -60%)',
  background: 'white',
  pointerEvents: 'none',
});

export interface Compound {
  smiles: string;
  name?: string;
  description?: string;
}
interface CompoundGridProps {
  prompt: string;
  originalCompoundSmiles: string;
  compounds: Analog[];
  selectedCompounds: Analog[];
  setSelectedCompounds: (compounds: Analog[]) => void;
  isFetchingAnalogExplanations: boolean;
}
export function CompoundGrid(props: CompoundGridProps) {
  const {
    prompt,
    originalCompoundSmiles,
    compounds,
    selectedCompounds,
    setSelectedCompounds,
    isFetchingAnalogExplanations,
  } = props;

  return (
    <Container>
      <OriginalCompound>
        <CompoundContainer original>
          <Compound smiles={originalCompoundSmiles} />
        </CompoundContainer>
        {prompt && (
          <SimplifiedTooltip content={prompt} contentProps={{ side: 'right', css: { padding: '$4' } }}>
            <Button text="Instructions" icon="text_snippet" weight="outlined" size="small" />
          </SimplifiedTooltip>
        )}
      </OriginalCompound>
      <GridContainer>
        {compounds.map(({ smiles, name, description }) => {
          const hasDescription = description;
          const isSelected = Boolean(selectedCompounds.find(s => s.smiles === smiles));
          return (
            <CompoundContainer
              selected={isSelected}
              onClick={() => {
                setSelectedCompounds(
                  isSelected
                    ? selectedCompounds.filter(s => s.smiles !== smiles)
                    : [...selectedCompounds, { smiles, description: '', name }]
                );
              }}
            >
              <Compound key={smiles} smiles={smiles} />
              {name && <Name>{name}</Name>}
              {hasDescription && (
                <Description>
                  <SimplifiedTooltip content={description} contentProps={{ css: { padding: '$4' } }} delayDuration={0}>
                    <Button icon="psychology_alt" weight="minimal" size="large" />
                  </SimplifiedTooltip>
                </Description>
              )}
              {!hasDescription && isFetchingAnalogExplanations && (
                <Description>
                  <FetchingExplanationSpinner size={13} />
                  <SimplifiedTooltip
                    content={
                      <>
                        <Spinner size={16} /> Generating explanation...
                      </>
                    }
                    contentProps={{
                      css: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: '$3',
                        padding: '$4',
                      },
                    }}
                    delayDuration={0}
                  >
                    <Button icon="psychology_alt" weight="minimal" size="large" />
                  </SimplifiedTooltip>
                </Description>
              )}
              <SelectionIcon icon={isSelected ? 'check' : 'clear'} selected={isSelected} />
            </CompoundContainer>
          );
        })}
      </GridContainer>
    </Container>
  );
}
